// Import carousel components
import './carousel/simple-carousel.js';

import { EmbedHtmlBlockComponent } from '@blocksuite/blocks';
import { css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { StyleInfo } from 'lit/directives/style-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  type PiqueEmbedHtmlModel,
} from './html-embed-block.js';

@customElement('pique-html-embed-block')
export class PiqueHtmlEmbedBlockComponent extends EmbedHtmlBlockComponent {
  override get model(): PiqueEmbedHtmlModel {
    return super.model as PiqueEmbedHtmlModel;
  }

  @query('.embed-html-block-iframe')
  private accessor iframe: HTMLIFrameElement | undefined = undefined;

  private _scrollListener?: (event: MessageEvent) => void;

  static override styles = css`
    :host {
      width: 100%;
      max-width: var(--affine-editor-width);
      margin: 0 auto;
      padding: 0 var(--affine-editor-side-padding);
      display: block;
    }

    /* Override parent styles to remove border */
    .affine-embed-html-block {
      border: none !important;
      background: transparent !important;
      padding: 0 !important;
      gap: 0 !important;
    }

    .embed-block-container {
      overflow: hidden;
    }

    .affine-embed-html-iframe-container {
      width: 100%;
      position: relative;
    }

    .embed-html-block-iframe {
      width: 100%;
      border: none;
      background: white;
    }

    .affine-embed-html {
      width: 100%;
    }

    .affine-embed-html-block {
      width: 100%;
    }

    .affine-embed-html-iframe-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    .affine-embed-html-iframe-overlay:not(.hide) {
      pointer-events: auto;
    }

    .affine-embed-html-iframe-overlay:hover {
      pointer-events: none;
    }

    .affine-embed-html-iframe-overlay.hide {
      display: none;
    }

    /* Styles for URL-based iframes */
    .url-embed {
      width: 100%;
      position: relative;
    }

    .url-embed .affine-embed-html-iframe-container {
      width: 100%;
      position: relative;
    }

    .url-embed .embed-html-block-iframe {
      width: 100%;
      height: 62vh;
      min-height: 500px;
      border: none;
    }

    /* Regular HTML content styles */
    .html-content {
      width: fit-content;
      max-width: 100%;
    }

    .html-content .affine-embed-html-iframe-container {
      width: fit-content;
      max-width: 100%;
    }

    .html-content .embed-html-block-iframe {
      width: 100%;
      height: auto;
      min-height: 100px;
    }

    /* Carousel styles */
    .html-carousel {
      width: 100%;
      position: relative;
    }

    .html-carousel .affine-embed-html {
      width: 100%;
      position: relative;
    }

    .html-carousel simple-carousel {
      width: 100%;
      height: 60vh;
      min-height: 480px;
      position: relative;
      z-index: 2;
    }

    .carousel-slide {
      width: 100%;
      aspect-ratio: 16 / 10;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      overflow: hidden;
    }

    .carousel-slide .embed-html-block-iframe {
      width: 100%;
      height: 100%;
      border: none;
      background: white;
      border-radius: 8px;
    }
  `;

  @state()
  protected accessor isSelected = false;

  @state()
  protected accessor showOverlay = true;

  protected isDragging = false;
  protected isResizing = false;

  protected embedHtmlStyle: StyleInfo = {};

  protected handleClick(event: MouseEvent) {
    event.stopPropagation();
    const selectionManager = this.host.selection;
    const blockSelection = selectionManager.create('block', {
      blockId: this.blockId,
    });
    selectionManager.setGroup('note', [blockSelection]);
  }

  protected handleDoubleClick(event: MouseEvent) {
    event.stopPropagation();
    this.iframeWrapper?.requestFullscreen().catch(console.error);
  }

  protected handleMouseEnter = () => {
    if (!this.isDragging && !this.isResizing) {
      this.showOverlay = false;
    }
  };

  protected handleMouseLeave = () => {
    if (!this.isSelected) {
      this.showOverlay = true;
    }
  };

  override connectedCallback() {
    super.connectedCallback();

    // Setup message listener for scroll sync
    this._scrollListener = (event: MessageEvent) => {
      // Only accept messages from our iframe
      if (event.source !== this.iframe?.contentWindow) return;

      // Handle scroll sync message
      if (event.data.type === 'scroll') {
        const { scrollHeight, clientHeight } = event.data;
        // Update our iframe's scroll position if needed
        if (this.iframe) {
          this.iframe.style.height = `${Math.max(scrollHeight, clientHeight)}px`;
          // Optionally scroll the parent page to keep the iframe in view
          const iframeRect = this.iframe.getBoundingClientRect();
          if (iframeRect.top < 0) {
            window.scrollBy(0, iframeRect.top);
          }
        }
      }
    };

    window.addEventListener('message', this._scrollListener);

    // Inject scroll tracking script into the iframe
    this.disposables.add(() => {
      if (this._scrollListener) {
        window.removeEventListener('message', this._scrollListener);
      }
    });

    this.disposables.add(
      this.std.selection.slots.changed.on(() => {
        this.isSelected =
          !!this.selected?.is('block') || !!this.selected?.is('surface');
        this.showOverlay =
          this.isResizing || this.isDragging || !this.isSelected;
      })
    );

    this.handleEvent('dragStart', () => {
      this.isDragging = true;
      this.showOverlay = true;
    });

    this.handleEvent('dragEnd', () => {
      this.isDragging = false;
      this.showOverlay = !this.isSelected;
    });
  }

  override firstUpdated(
    changedProperties: Map<string | number | symbol, unknown>
  ) {
    super.firstUpdated?.(changedProperties);

    if (this.iframe && this.model.url) {
      // Inject scroll tracking script
      this.iframe.addEventListener('load', () => {
        try {
          this.iframe?.contentWindow?.postMessage(
            {
              type: 'injectScrollTracker',
              script: `
              // Track scroll position
              window.addEventListener('scroll', () => {
                window.parent.postMessage({
                  type: 'scroll',
                  scrollTop: window.scrollY,
                  scrollHeight: document.documentElement.scrollHeight,
                  clientHeight: document.documentElement.clientHeight
                }, '*');
              });

              // Track dynamic content changes
              const observer = new MutationObserver(() => {
                window.parent.postMessage({
                  type: 'scroll',
                  scrollTop: window.scrollY,
                  scrollHeight: document.documentElement.scrollHeight,
                  clientHeight: document.documentElement.clientHeight
                }, '*');
              });

              observer.observe(document.body, {
                childList: true,
                subtree: true
              });

              // Initial size report
              window.parent.postMessage({
                type: 'scroll',
                scrollTop: window.scrollY,
                scrollHeight: document.documentElement.scrollHeight,
                clientHeight: document.documentElement.clientHeight
              }, '*');
            `,
            },
            '*'
          );
        } catch (e) {
          console.warn('Failed to inject scroll tracker:', e);
        }
      });
    }
  }

  override renderBlock() {
    const { style } = this.model;
    this._cardStyle = style;

    // Parse design JSON if it exists
    let designObj;
    try {
      designObj = this.model.design ? JSON.parse(this.model.design) : null;
    } catch (e) {
      console.warn('Failed to parse design JSON:', e);
      designObj = null;
    }

    // Use the embedHtmlStyle directly from design
    this.embedHtmlStyle = designObj?.embedHtmlStyle || {};

    return this.renderEmbed(() => {
      // Check if we have multiple HTML slides (carousel mode)
      if (this.model.htmlSlides && Array.isArray(this.model.htmlSlides) && this.model.htmlSlides.length > 0) {
        return this.renderCarousel();
      }

      if (!this.model.html && !this.model.url) {
        return html` <div class="affine-html-empty">Empty</div>`;
      }

      // If URL is provided, use it directly in the iframe
      if (this.model.url) {
        // Combine specific iframe styles with the general embedHtmlStyle
        const iframeStyle = {
          ...this.embedHtmlStyle,
          width: '100%',
          height: '100%',
        };
        return html`
          <div
            class=${classMap({
          'affine-embed-html-block': true,
          'url-embed': true,
          selected: this.isSelected,
        })}
            @click=${this.handleClick}
            @dblclick=${this.handleDoubleClick}
          >
            <div class="affine-embed-html">
              <div class="affine-embed-html-iframe-container">
                <div class="embed-html-block-iframe-wrapper" allowfullscreen>
                  <iframe
                    class="embed-html-block-iframe"
                    style=${styleMap(iframeStyle)}
                    src=${this.model.url}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-downloads allow-modals"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    loading="lazy"
                  ></iframe>
                  <embed-html-fullscreen-toolbar
                    .embedHtml=${this}
                  ></embed-html-fullscreen-toolbar>
                </div>
                <div
                  class=${classMap({
          'affine-embed-html-iframe-overlay': true,
          hide: !this.showOverlay,
        })}
                ></div>
              </div>
            </div>
          </div>
        `;
      }

      // If no URL but HTML content exists, use srcdoc
      const htmlSrc = `
        <style>
          body {
            margin: 0;
            height: 100%;
            overflow: auto;
          }
          * {
            box-sizing: border-box;
          }
        </style>
        ${this.model.html}
      `;

      // Combine specific iframe styles with the general embedHtmlStyle
      const iframeStyle = {
        ...this.embedHtmlStyle,
        width: '100%',
        height: '100%',
      };

      return html`
        <div
          class=${classMap({
        'affine-embed-html-block': true,
        'html-content': true,
        selected: this.isSelected,
      })}
          @click=${this.handleClick}
          @dblclick=${this.handleDoubleClick}
        >
          <div class="affine-embed-html">
            <div class="affine-embed-html-iframe-container">
              <div class="embed-html-block-iframe-wrapper" allowfullscreen>
                <iframe
                  class="embed-html-block-iframe"
                  style=${styleMap(iframeStyle)}
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  scrolling="auto"
                  .srcdoc=${htmlSrc}
                ></iframe>
                <embed-html-fullscreen-toolbar
                  .embedHtml=${this}
                ></embed-html-fullscreen-toolbar>
              </div>
              <div
                class=${classMap({
        'affine-embed-html-iframe-overlay': true,
        hide: !this.showOverlay,
      })}
              ></div>
            </div>
          </div>
        </div>
      `;
    });
  }

  private renderCarousel() {
    if (!this.model.htmlSlides || !Array.isArray(this.model.htmlSlides)) {
      return html`<div class="affine-html-empty">No slides available</div>`;
    }

    // Combine specific iframe styles with the general embedHtmlStyle
    const iframeStyle = {
      ...this.embedHtmlStyle,
      width: '100%',
      height: '100%',
    };

    return html`
      <div
        class=${classMap({
      'affine-embed-html-block': true,
      'html-carousel': true,
      selected: this.isSelected,
    })}
        @click=${this.handleClick}
        @dblclick=${this.handleDoubleClick}
      >
        <div class="affine-embed-html">
          <simple-carousel .host=${this.host}>
            ${this.model.htmlSlides.map((slideHtml, index) => {
      // The slideHtml already contains all necessary styles.
      // We make it responsive by replacing fixed width/height with 100%.
      const responsiveSlideHtml = slideHtml
        .replace(/width="[^"]*"/, 'width="100%"')
        .replace(/height="[^"]*"/, 'height="100%"');
      return html`
                <div class="carousel-slide" data-slide-index="${index}">
                  <iframe
                    class="embed-html-block-iframe"
                    style=${styleMap(iframeStyle)}
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    scrolling="auto"
                    .srcdoc=${responsiveSlideHtml}
                  ></iframe>
                </div>
              `;
    })}
          </simple-carousel>
          <div
            class=${classMap({
      'affine-embed-html-iframe-overlay': true,
      hide: !this.showOverlay,
    })}
          ></div>
        </div>
      </div>
    `;
  }
}
